import { useEffect, useMemo, useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';

// T-282 — Home del PREPOSTO: dashboard a tile in stile TrackEAT.
// 3 tile: Tracciabilità (completa, per i controlli ASL), DDT, Ordini.

interface Tile {
  key: string;
  icon: string;
  title: string;
  subtitle: string;
  grad: string;
  route: string;
}
const TILES: Tile[] = [
  {
    key: 'tracciabilita',
    icon: '🔎',
    title: 'Tracciabilità',
    subtitle: 'Filiera completa della merce ricevuta (per i controlli)',
    grad: 'linear-gradient(135deg,#1f6f5c,#27a37f)',
    route: '/preposto/tracciabilita',
  },
  {
    key: 'ddt',
    icon: '📄',
    title: 'DDT',
    subtitle: 'Documenti di trasporto ricevuti',
    grad: 'linear-gradient(135deg,#1f4e8c,#2e74c2)',
    route: '/preposto/ddt',
  },
  {
    key: 'ordini',
    icon: '🧾',
    title: 'Ordini',
    subtitle: 'Invia la richiesta pasti (quantità per portata)',
    grad: 'linear-gradient(135deg,#9a6212,#e0992f)',
    route: '/preposto/ordini',
  },
  {
    key: 'ordini-letto',
    icon: '🛏️',
    title: 'Ordine per letto',
    subtitle: 'Inserisci i letti di ogni reparto (un letto = una dieta)',
    grad: 'linear-gradient(135deg,#6b3fa0,#8a63d2)',
    route: '/preposto/ordini-letto',
  },
  {
    key: 'riepilogo',
    icon: '📋',
    title: 'Riepilogo e invio',
    subtitle: 'Vedi tutti gli ordini dei reparti, controlla e invia al centro cottura',
    grad: 'linear-gradient(135deg,#0b6b63,#12a594)',
    route: '/preposto/riepilogo',
  },
];

export default function PrepostoHomePage(): JSX.Element {
  const { profile, signOut } = useAuth();
  const sb = useMemo(() => getSupabase(), []);
  const router = useIonRouter();
  const [destName, setDestName] = useState('');

  useEffect(() => {
    const id = profile?.preposto?.destination_id;
    if (!id) return;
    let mounted = true;
    void (async () => {
      const { data } = await sb.from('destinations').select('name').eq('id', id).maybeSingle();
      if (mounted) setDestName((data as { name?: string } | null)?.name ?? '');
    })();
    return () => {
      mounted = false;
    };
  }, [sb, profile]);

  return (
    <IonPage>
      <style>{`
        .ph-hero{background:linear-gradient(135deg,#1f4e5c,#2e7d8a);color:#fff;border-radius:16px;padding:18px;margin:4px 0 16px;box-shadow:0 4px 14px rgba(0,0,0,.12)}
        .ph-hero .k{opacity:.8;font-size:12px;letter-spacing:.06em;text-transform:uppercase}
        .ph-hero h2{margin:2px 0 0;font-size:21px;font-weight:800}
        .ph-grid{display:grid;grid-template-columns:1fr;gap:14px}
        .ph-tile{display:flex;align-items:center;gap:14px;border:none;text-align:left;width:100%;color:#fff;border-radius:16px;padding:18px;box-shadow:0 3px 12px rgba(0,0,0,.14);cursor:pointer}
        .ph-tile .ic{font-size:32px;line-height:1}
        .ph-tile .tt{font-size:18px;font-weight:800}
        .ph-tile .st{font-size:13px;opacity:.92;margin-top:2px}
        .ph-tile:active{transform:scale(.99)}
      `}</style>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Il mio portale</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void signOut()}>Esci</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="ph-hero">
          <div className="k">Struttura</div>
          <h2>{destName || 'La mia struttura'}</h2>
        </div>
        <div className="ph-grid">
          {TILES.map((t) => (
            <button key={t.key} type="button" className="ph-tile" style={{ background: t.grad }} onClick={() => router.push(t.route, 'forward')}>
              <span className="ic">{t.icon}</span>
              <span>
                <div className="tt">{t.title}</div>
                <div className="st">{t.subtitle}</div>
              </span>
            </button>
          ))}
        </div>
      </IonContent>
    </IonPage>
  );
}
